
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Services from "./pages/Services";
import ProductVideos from "./pages/ProductVideos";
import SocialMediaAds from "./pages/SocialMediaAds";
import WhyFutureLevel from "./pages/WhyFutureLevel";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import Legal from "./pages/Legal";
import Terms from "./pages/Terms";
import CookieBanner from "./components/CookieBanner";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <LanguageProvider>
          <Toaster />
          <Sonner />
          <CookieBanner />
          <Routes>
            {/* Default route - redirect to preferred language */}
            <Route path="/" element={<Navigate to="/de" replace />} />
            
            {/* Language-specific root pages */}
            <Route path="/:lang" element={<Index />} />
            
            {/* Blog routes */}
            <Route path="/:lang/blog" element={<Blog />} />
            <Route path="/:lang/blog/:slug" element={<BlogPost />} />
            
            {/* Generic blog route redirects to language-specific blog */}
            <Route path="/blog" element={<Navigate to="/de/blog" replace />} />
            
            {/* Service pages */}
            <Route path="/de/leistungen" element={<Services />} />
            <Route path="/en/services" element={<Services />} />
            <Route path="/fr/services" element={<Services />} />
            <Route path="/it/servizi" element={<Services />} />
            
            {/* Generic service routes redirect to language-specific ones */}
            <Route path="/services" element={<Navigate to="/en/services" replace />} />
            <Route path="/services/product-videos" element={<Navigate to="/en/services/product-videos" replace />} />
            <Route path="/services/productvideos" element={<Navigate to="/en/services/product-videos" replace />} />
            <Route path="/services/social-media-ads" element={<Navigate to="/en/services/social-media-ads" replace />} />
            <Route path="/services/socialmediaads" element={<Navigate to="/en/services/social-media-ads" replace />} />
            
            {/* Specific service pages */}
            {/* Product Videos - all language paths */}
            <Route path="/en/services/product-videos" element={<ProductVideos />} />
            <Route path="/de/leistungen/produktvideos" element={<ProductVideos />} />
            <Route path="/fr/services/videos-de-produits" element={<ProductVideos />} />
            <Route path="/it/servizi/video-di-prodotti" element={<ProductVideos />} />
            
            {/* Social Media Ads - all language paths */}
            <Route path="/en/services/social-media-ads" element={<SocialMediaAds />} />
            <Route path="/de/leistungen/social-media-ads" element={<SocialMediaAds />} />
            <Route path="/fr/services/publicites-medias-sociaux" element={<SocialMediaAds />} />
            <Route path="/it/servizi/pubblicita-social-media" element={<SocialMediaAds />} />
            
            {/* Why Future Level page */}
            <Route path="/why-future-level" element={<WhyFutureLevel />} />
            <Route path="/de/warum-future-level" element={<WhyFutureLevel />} />
            <Route path="/en/why-future-level" element={<WhyFutureLevel />} />
            <Route path="/fr/pourquoi-future-level" element={<WhyFutureLevel />} />
            <Route path="/it/perche-future-level" element={<WhyFutureLevel />} />
            
            {/* About pages */}
            <Route path="/de/ueber-uns" element={<About />} />
            <Route path="/en/about" element={<About />} />
            <Route path="/fr/a-propos" element={<About />} />
            <Route path="/it/chi-siamo" element={<About />} />
            
            {/* Contact pages */}
            <Route path="/de/kontakt" element={<Contact />} />
            <Route path="/en/contact" element={<Contact />} />
            <Route path="/fr/contact" element={<Contact />} />
            <Route path="/it/contatto" element={<Contact />} />
            
            {/* Privacy pages */}
            <Route path="/de/datenschutz" element={<Privacy />} />
            <Route path="/en/privacy-policy" element={<Privacy />} />
            <Route path="/fr/politique-de-confidentialite" element={<Privacy />} />
            <Route path="/it/informativa-privacy" element={<Privacy />} />
            
            {/* Legal pages */}
            <Route path="/de/impressum" element={<Legal />} />
            <Route path="/en/legal" element={<Legal />} />
            <Route path="/fr/mentions-legales" element={<Legal />} />
            <Route path="/it/note-legali" element={<Legal />} />
            
            {/* Terms pages */}
            <Route path="/de/agb" element={<Terms />} />
            <Route path="/en/terms" element={<Terms />} />
            <Route path="/fr/conditions" element={<Terms />} />
            <Route path="/it/termini" element={<Terms />} />
            
            {/* Generic redirect */}
            <Route path="/agb" element={<Navigate to="/de/agb" replace />} />
            <Route path="/datenschutz" element={<Navigate to="/de/datenschutz" replace />} />
            <Route path="/impressum" element={<Navigate to="/de/impressum" replace />} />
            
            {/* Catch-all route with language parameter */}
            <Route path="/:lang/*" element={<NotFound />} />
            
            {/* Catch-all route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </LanguageProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
